import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { HiArrowLeft, HiPhotograph, HiPlay, HiX, HiChevronLeft, HiChevronRight, HiExternalLink } from 'react-icons/hi';
import { useLanguage } from '../i18n/LanguageContext';
import './ProjectsPage.css';

const Lightbox = ({ items, index, onClose, onPrev, onNext }) => {
  const item = items[index];

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <motion.div
      className="lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <button className="lightbox__close" onClick={onClose} aria-label="Close">
        <HiX />
      </button>

      {items.length > 1 && (
        <>
          <button
            className="lightbox__nav lightbox__nav--prev"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            aria-label="Previous"
          >
            <HiChevronLeft />
          </button>
          <button
            className="lightbox__nav lightbox__nav--next"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            aria-label="Next"
          >
            <HiChevronRight />
          </button>
        </>
      )}

      <motion.div
        className="lightbox__content"
        key={index}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        {item.type === 'image' ? (
          <img src={item.src} alt={item.alt || ''} className="lightbox__img" />
        ) : (
          <video
            src={item.src}
            className="lightbox__video"
            controls
            autoPlay
            poster={item.poster || undefined}
          />
        )}
      </motion.div>

      {items.length > 1 && (
        <div className="lightbox__counter">
          {index + 1} / {items.length}
        </div>
      )}
    </motion.div>
  );
};

const ProjectsPage = () => {
  const { t } = useLanguage();
  const items = t('projects.items');
  const location = useLocation();
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const openLightbox = (showcaseItems, index) => {
    setLightbox({ items: showcaseItems, index });
  };

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const prevImage = useCallback(() => {
    setLightbox((prev) => {
      if (!prev) return null;
      return { ...prev, index: (prev.index - 1 + prev.items.length) % prev.items.length };
    });
  }, []);

  const nextImage = useCallback(() => {
    setLightbox((prev) => {
      if (!prev) return null;
      return { ...prev, index: (prev.index + 1) % prev.items.length };
    });
  }, []);

  return (
    <div className="projects-page">
      <div className="container">
        <motion.div
          className="projects-page__nav"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/" className="projects-page__back">
            <HiArrowLeft /> {t('projects.backToHome')}
          </Link>
        </motion.div>

        <motion.div
          className="projects-page__header"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-label">{t('projects.label')}</span>
          <h1 className="section-title">
            {t('projects.titleStart')}
            <span className="gradient-text">{t('projects.titleHighlight')}</span>
          </h1>
          <p className="section-subtitle">{t('projects.subtitle')}</p>
        </motion.div>

        <div className="projects-page__list">
          {items.map((project, i) => (
            <motion.article
              key={project.id}
              id={project.id}
              className="project-detail"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="project-detail__top">
                <span className="project-detail__number">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="project-detail__title-row">
                  <h2 className="project-detail__title">{project.title}</h2>
                  {project.badge && (
                    <span className="project-detail__badge">
                      {t(`projects.${project.badge}`)}
                    </span>
                  )}
                </div>
              </div>

              <div className="project-detail__content">
                <div className="project-detail__description">
                  <p>{project.description}</p>
                  {project.itchUrl && (
                    <a
                      href={project.itchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-detail__store-link"
                    >
                      <HiExternalLink /> {project.itchLabel}
                    </a>
                  )}
                  {project.storeUrl && (
                    <a
                      href={project.storeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="project-detail__store-link project-detail__store-link--playstore"
                    >
                      <HiExternalLink /> {project.storeLabel}
                    </a>
                  )}
                </div>

                <div className="project-detail__columns">
                  <div className="project-detail__section">
                    <h3 className="project-detail__label">
                      {t('projects.keyFeaturesLabel')}
                    </h3>
                    <ul className="project-detail__features">
                      {project.keyFeatures.map((feature, j) => (
                        <li key={j}>
                          <span className="project-detail__bullet" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="project-detail__section">
                    <h3 className="project-detail__label">
                      {t('projects.techStackLabel')}
                    </h3>
                    <div className="project-detail__tech">
                      {project.techStack.map((tech, j) => (
                        <span key={j} className="project-detail__tech-badge">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {project.showcase && project.showcase.length > 0 && (
                  <div className="project-detail__section project-detail__showcase-section">
                    <h3 className="project-detail__label">
                      {t('projects.showcaseLabel')}
                    </h3>
                    <div className="project-detail__showcase">
                      {project.showcase.map((item, j) => (
                        <div
                          key={j}
                          className="project-detail__showcase-item"
                          onClick={() => openLightbox(project.showcase, j)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => e.key === 'Enter' && openLightbox(project.showcase, j)}
                        >
                          {item.type === 'image' ? (
                            <>
                              <img
                                src={item.src}
                                alt={item.alt || project.title}
                                className="project-detail__showcase-img"
                                loading="lazy"
                              />
                              <div className="project-detail__showcase-icon">
                                <HiPhotograph />
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="project-detail__showcase-video-thumb">
                                <video
                                  src={item.src}
                                  className="project-detail__showcase-video"
                                  preload="metadata"
                                  poster={item.poster || undefined}
                                />
                                <div className="project-detail__showcase-play">
                                  <HiPlay />
                                </div>
                              </div>
                            </>
                          )}
                          {item.caption && (
                            <p className="project-detail__showcase-caption">
                              {item.caption}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <Lightbox
            items={lightbox.items}
            index={lightbox.index}
            onClose={closeLightbox}
            onPrev={prevImage}
            onNext={nextImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectsPage;
